import { useEffect, useCallback, useState, useMemo } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { useStoreValue, TEMPORARY_STATUSES } from 'store';
import { config } from '_config';

import { useHistory } from 'react-router-dom';
import { ALL_APPLICATION_FIELDS } from 'gql';
import { useErrorTrackingAndUpdateStore } from '../useErrorTrackingAndUpdateStore';
import { calculateNextStep } from './calculateNextStep';
import { updateStoreAndRedirect } from './updateStoreAndRedirect';

/**
 * Wait for an application's status changes to a new state. Then process the result and redirect to the next page.
 * Long polling is used to check the status of the application via the `application` GraphQL query.
 * It considers the status is new when the status is not one of the temporary statuses from constant `TEMPORARY_STATUSES`,
 * or the status is changed from the first fetched initial status.
 */
export function useWaitAndRedirect({
  applicationId,
  testId,
  longPolling = true,
  startOnMount = true,
  calculateNextStep: argCalculateNextStep,
}) {
  const GET_APPLICATION_GQL = useMemo(
    () => gql`
      ${ALL_APPLICATION_FIELDS}

      query ${testId}${config.countryCode}($id: ID!) {
        application(id: $id) {
          ...AllFields
        }
      }
    `,
    [testId],
  );

  const history = useHistory();
  const [initialStatus, setInitialStatus] = useState();
  const [loading, setLoading] = useState(false);
  const [storeState, updateStore] = useStoreValue();
  const { handleErrorCallback: handleError } = useErrorTrackingAndUpdateStore(
    `hook:useWaitAndProcess:${testId}:${config.countryCode}`,
  );
  const onNewStatus = useCallback(
    application => {
      const nextStep = argCalculateNextStep
        ? argCalculateNextStep(application, storeState)
        : calculateNextStep(application, storeState);
      updateStoreAndRedirect({ application, nextStep, updateStore, history, storeState });
    },
    [argCalculateNextStep, history, storeState, updateStore],
  );

  const [query, { data, error, stopPolling, refetch }] = useLazyQuery(GET_APPLICATION_GQL, {
    pollInterval: 1000,
    fetchPolicy: 'network-only',
  });

  const start = useCallback(() => {
    if (!applicationId) {
      throw new Error(`Unexpected applicationId: ${applicationId}`);
    }

    if (loading === false) {
      setLoading(true);
      query({ variables: { id: applicationId } });
    }
  }, [applicationId, loading, query]);

  useEffect(() => {
    if (startOnMount) {
      start();
    }
  }, [applicationId, query, start, startOnMount]);

  useEffect(() => {
    if (data) {
      const { application } = data;

      if (!initialStatus) {
        setInitialStatus(application.status);
      }

      // Check if application status changed or is no longer temporary
      const isStatusChanged = application.status !== initialStatus && initialStatus !== undefined;
      const isNotTemporaryStatus = !TEMPORARY_STATUSES.includes(application.status);

      // Calculate next step to check if there's a new step to navigate to
      const nextStep = argCalculateNextStep
        ? argCalculateNextStep(application, storeState)
        : calculateNextStep(application, storeState);
      const hasNewStep = nextStep !== storeState.activeStep;

      const isReadyForNextStep = (isNotTemporaryStatus || isStatusChanged) && hasNewStep;

      if (!longPolling || isReadyForNextStep) {
        stopPolling();
        setLoading(false);
        onNewStatus(application);
      }
      return;
    }
    if (error) {
      stopPolling();
      setLoading(false);
      handleError(error);
    }
  }, [
    data,
    error,
    stopPolling,
    onNewStatus,
    handleError,
    initialStatus,
    longPolling,
    argCalculateNextStep,
    storeState,
  ]);

  const retry = useCallback(() => {
    setLoading(true);
    refetch();
  }, [refetch]);

  const stop = useCallback(() => {
    stopPolling();
    setLoading(false);
  }, [stopPolling]);

  return {
    loading,
    retry,
    stop,
    start,
  };
}
